import { 
    useState, 
    useEffect, 
} from "react";
import { 
    useTheme,
    useMediaQuery,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    TextField,
    IconButton,
} from "@mui/material";
import { 
    Clear,
    UpdateRounded, 
} from '@mui/icons-material';
import { 
    BarChart, 
    LineChart, 
} from "@mui/x-charts";
import axios from "axios";
import { useSnackbar } from "notistack";
import { withPather } from "react-pather";
import { 
    PeriodOptions, 
    periodOptions, 
    ChartSwitcher,
    ChartType,
} from "./common";
import Loader from "../../../Loader";

const metrics = [
    { label: 'Users & Phantoms', value: 'users_phantoms' },
    { label: 'Users & Phantoms Separated', value: 'users_phantoms_separated' },
    { label: 'Registered Users (Active)', value: 'registered_users_active' },  // UserMarker.ACTIVE
    { label: 'Registered Users (Activated)', value: 'registered_users_activated' },  // UserMarker.ACTIVE or UserMarker.DECTIVATED 
    { label: 'Registered Users (Non-activated)', value: 'registered_users_non_activated' },  // UserMarker.CREATED
    { label: 'Anonymous Users (Phantoms)', value: 'anonymous_users' },
    { label: 'Deactivated Users', value: 'deactivated_users' },
    { label: 'Users Saldo (Registered (Active) - Deactivated)', value: 'users_saldo' },
    { label: 'Users Last Sign In', value: 'users_last_sign_in' },
    { label: 'Conversions', value: 'conversions' },
    { label: 'Conversions Seperated', value: 'conversions_separated' },
    { label: 'Conversions Deleted', value: 'conversions_deleted' },
    { label: 'Conversions Seperated & Deleted', value: 'conversions_separated_deleted' },
    { label: 'Conversions Saldo (Created - Deleted)', value: 'conversions_saldo' },
    { label: 'Redirects', value: 'redirects' },
    { label: 'Redirects Separated', value: 'redirects_separated' },
    { label: 'Redirects Successful', value: 'redirects_successful' },
    { label: 'Redirects Failed', value: 'redirects_failed' },
    { label: 'Redirects Saldo (Successful - Failed)', value: 'redirects_saldo' },
];

const Metrics = ({ pather }) => {

    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const [chartType, setChartType] = useState(ChartType.BAR);

    const [loading, setLoading] = useState(false);
    const [period, setPeriod] = useState(periodOptions[0]);
    const [metric, setMetric] = useState(metrics[0].value);
    const [delta, setDelta] = useState(null);
    
    const [info, setInfo] = useState({});
    const [series, setSeries] = useState([]);
    const [labels, setLabels] = useState([]);

    const matchesDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const matchesUpSm = useMediaQuery(theme.breakpoints.up('sm'));

    const setInfoSafe = updStateObj => {
        setInfo(prevState => ({
            ...prevState,
            ...updStateObj,
        }));    
    }

    useEffect(() => {
        setInfo({});
        series.forEach(serie => {
            setInfoSafe({
                [serie.label]: serie.data.reduce((accumulator, currentValue) => accumulator + currentValue, 0), 
            })
        });
    }, [series]);

    const fetchMetrics = () => {
        setLoading(true);
        axios.get(
            pather.back.Settings.metrics, 
            { 
                params: { 
                    metric,
                    delta,
                    period: period.value,
                }
            }
        )
        .then(response => {
            const data = response.data;
            console.log(data);
            setLabels(data?.labels);
            setSeries(data?.series);
        })
        .catch(error => {
            console.log(error);
            const status = error?.response?.data?.status;
            status && enqueueSnackbar(status, { variant: 'error' });
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        fetchMetrics();
    }, [metric, period, delta]);

    const bar = (
        <BarChart
            xAxis={[{ data: labels, scaleType: 'band' }]}
            series={series}
            width={matchesDownMd ? (matchesUpSm ? 600 : 450) : 900}
            height={matchesDownMd ? (matchesUpSm ? 500 : 350) : 600}
        />
    );

    const timeline = (
        <LineChart
            xAxis={[{ data: labels, scaleType: 'point' }]}
            series={series}
            width={matchesDownMd ? (matchesUpSm ? 600 : 450) : 900}
            height={matchesDownMd ? (matchesUpSm ? 500 : 350) : 600}
        />
    );

    const charts = {
        bar,
        timeline,
    }

    return (
        <>
            {
                loading ? 
                <Loader/> :
                <Box sx={{display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 3}}>
                    <Box sx={{width: '100%', display: 'flex'}}>
                        <TopBar
                            metric={metric}
                            setMetric={setMetric}
                            delta={delta}
                            setDelta={setDelta}
                            chartType={chartType}
                            setChartType={setChartType}
                            reload={fetchMetrics}
                        />
                    </Box>
                    <Box>
                        <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                            <Box sx={{display: 'flex', gap: 5}}>
                                {
                                    Object.keys(info).map((label, index) => {
                                        return (
                                            <Typography variant="h6" component="div">
                                                {label}: {info[label]}
                                            </Typography>
                                        );
                                    })
                                }
                            </Box>
                        </Box>
                        <Box sx={{display: 'flex'}}>
                            {   
                                series?.length > 0 && labels?.length > 0 ?
                                charts[chartType] :
                                <Box sx={{
                                    height: '50vh', 
                                    width: '100%', 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center'
                                }}>
                                    <Typography variant="h6" component="div">
                                        No data
                                    </Typography>
                                </Box>
                            }
                        </Box>
                        <Box>
                            <PeriodOptions 
                                period={period} 
                                setPeriod={setPeriod} 
                                size={matchesDownMd ? 'small' : 'medium'}
                                extended={true}
                            />
                        </Box>
                    </Box>
                </Box>
            }
        </>
    );
}

const TopBar = props => {
    const { 
        metric, 
        setMetric, 
        delta, 
        setDelta, 
        chartType, 
        setChartType, 
        reload,
    } = props;
    return (
        <Box sx={{width: '100%', display: 'flex', gap: 5, justifyContent: 'center', alignItems: 'center'}}>
            <Box sx={{ minWidth: '15vw' }}>
                <FormControl fullWidth>
                    <InputLabel id="select-metric-label">Metric</InputLabel>
                    <Select
                        labelId="select-metric-label"
                        id="select-metric-field"
                        value={metric}
                        label="Metric"
                        onChange={e => setMetric(e.target.value)}
                    >
                        {
                            metrics.map(metric => {
                                return (
                                    <MenuItem value={metric.value} key={metric.value}>{metric.label}</MenuItem>
                                );
                            })
                        }
                    </Select>
                </FormControl>
            </Box>
            <Box>
                <TextField 
                    label="Delta" 
                    variant="standard" 
                    type="number"
                    value={delta}
                    onChange={e => setDelta(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            delta &&
                            <IconButton onClick={() => setDelta(null)}>
                                <Clear/>
                            </IconButton>
                        )
                    }}
                />
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                <ChartSwitcher 
                    chartType={chartType}
                    setChartType={setChartType}
                    size='large'
                />
                <Box>
                    <IconButton onClick={reload} sx={{cursor: 'pointer'}}>
                        <UpdateRounded sx={{fontSize: '30px'}}/>
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
}

export default withPather()(Metrics);